import { Clock, Cube, HeartStraight, MapPin } from '@phosphor-icons/react';
import React, { Fragment, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { InventoryStatusString } from '../../../@types/ElasticVehicle';
import { numberSeparator } from '../../../helpers/formats';
import BulletSeparatedList from '../BulletSeparatedList';
import { SrOnly } from '../SrOnly';
import {
  Body,
  EcomBanner,
  Footer,
  FooterMain,
  FooterMainLogo,
  Image,
  ImageBackdrop,
  Location,
  Media,
  PriceLabelAlt,
  PriceLabelMain,
  PriceValueAlt,
  PriceValueMain,
  SaveButton,
  Seller,
  Status,
  StatusBadge,
  Title,
  Wrapper,
} from './wrapper';

interface ProductCardImageSrcSetProps {
  url: string;
  width: number;
}

export interface ProductCardImageProps {
  src: string;
  useBackdrop?: boolean;
  srcSet?: ProductCardImageSrcSetProps[];
  webp?: ProductCardImageSrcSetProps[];
  sizes?: string;
}

interface ProductCardGuaranteePackageLogoProps {
  src: string;
  srcSet?: string;
  alt: string;
}

interface ProductCardPriceProps {
  label: string;
  value: number;
  currency: string;
}

export interface ProductCardBaseProps {
  adStatus?: string;
  title: string;
  image: ProductCardImageProps;
  location: string;
  sellerName: string;
  inventoryStatus?: InventoryStatusString;
  uspList?: string[];
  price?: ProductCardPriceProps[];
  guaranteePackageLogo?: ProductCardGuaranteePackageLogoProps;
  buyOnline?: boolean | null;
  testId?: string;
  save?: {
    isSaved?: boolean;
    onClick: () => void;
  };
  onClick?: () => void;
}

interface ProductCardProps extends ProductCardBaseProps {
  to: string;
  forwardedRef?: Dispatch<SetStateAction<HTMLDivElement | null>>;
}

const ProductCard = ({
  adStatus,
  to,
  title,
  image,
  location,
  sellerName,
  uspList,
  inventoryStatus,
  price,
  guaranteePackageLogo,
  buyOnline,
  testId,
  save,
  forwardedRef,
  onClick,
}: ProductCardProps) => {
  const { t } = useTranslation(['common', 'other']);

  const saveButtonTitle = save?.isSaved
    ? t('removeSavedAd', { ns: 'other' })
    : t('saveAd', { ns: 'other' });

  const sold = adStatus === 'Sold';

  return (
    <Wrapper ref={forwardedRef} data-testid={testId}>
      <Body>
        <Media>
          <picture>
            {image.webp && (
              <source
                type="image/webp"
                srcSet={image.webp.map((webp) => `${webp.url} ${webp.width}w`).join(', ')}
                sizes={image.sizes}
              />
            )}
            {image.srcSet ? (
              <Image
                srcSet={image.srcSet.map((srcSet) => `${srcSet.url} ${srcSet.width}w`).join(', ')}
                sizes={image.sizes}
                src={image.src}
                alt={title}
              />
            ) : (
              <Image src={image.src} alt={title} />
            )}
          </picture>
          {image.useBackdrop && <ImageBackdrop src={image.src} alt={title} aria-hidden="true" />}
          {save && (
            <SaveButton onClick={save.onClick} title={saveButtonTitle}>
              <HeartStraight size="1em" weight={save.isSaved ? 'fill' : 'bold'} />
              <SrOnly>{saveButtonTitle}</SrOnly>
            </SaveButton>
          )}
        </Media>
        <Status>
          <Location data-testid={testId ? `${testId}-location` : undefined}>
            <MapPin size={14} weight="bold" /> {location}
          </Location>
          {sold ? (
            <StatusBadge $tone="negative" $size="small">
              {t('sold', { ns: 'common' })}
            </StatusBadge>
          ) : (
            <>
              {inventoryStatus === 'InStock' && (
                <StatusBadge $tone="positive" $size="small">
                  {t('inStock', { ns: 'common' })}
                  <Cube size={16} weight="bold" />
                </StatusBadge>
              )}
              {inventoryStatus === 'Incoming' && (
                <StatusBadge $tone="neutral" $size="small">
                  {t('incoming', { ns: 'common' })}
                  <Clock size={16} weight="bold" />
                </StatusBadge>
              )}
              {inventoryStatus === 'Order' && (
                <StatusBadge $tone="neutral" $size="small">
                  {t('forOrder', { ns: 'common' })}
                  <Clock size={16} weight="bold" />
                </StatusBadge>
              )}
            </>
          )}
        </Status>
        <Seller data-testid={testId ? `${testId}-seller` : undefined}>{sellerName}</Seller>
        <Title
          to={to}
          title={title}
          onClick={onClick}
          data-testid={testId ? `${testId}-link` : undefined}
        >
          {title}
        </Title>
        {uspList && <BulletSeparatedList items={uspList} size="small" />}
      </Body>
      {(price || guaranteePackageLogo) && (
        <Footer>
          <FooterMain>
            {price && price.length > 0 && (
              <div>
                <PriceLabelMain>{price[0].label}</PriceLabelMain>
                <PriceValueMain data-testid={testId ? `${testId}-price` : undefined}>
                  {numberSeparator(price[0].value)} {price[0].currency}
                </PriceValueMain>
              </div>
            )}
            {guaranteePackageLogo && (
              <FooterMainLogo
                src={guaranteePackageLogo.src}
                srcSet={guaranteePackageLogo.srcSet}
                alt={guaranteePackageLogo.alt}
              />
            )}
          </FooterMain>
          {price && price.slice(1).length > 0 && (
            <BulletSeparatedList
              items={price.slice(1).map((priceItem, idx) => (
                <Fragment key={idx}>
                  <PriceLabelAlt as="span">{priceItem.label}</PriceLabelAlt>
                  <PriceValueAlt as="span">
                    {numberSeparator(priceItem.value)} {priceItem.currency}
                  </PriceValueAlt>
                </Fragment>
              ))}
              size="small"
            />
          )}
          {false && buyOnline && (
            <EcomBanner>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Ecom Banner</title>
                <path
                  d="M4.16699 3.75H5.83366V11.25H14.167L15.8337 5.41667H5.83366"
                  stroke="currentColor"
                  strokeWidth="1.66667"
                  strokeLinecap="square"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.83366 16.25C6.75413 16.25 7.50033 15.5038 7.50033 14.5833C7.50033 13.6629 6.75413 12.9167 5.83366 12.9167C4.91318 12.9167 4.16699 13.6629 4.16699 14.5833C4.16699 15.5038 4.91318 16.25 5.83366 16.25Z"
                  fill="currentColor"
                />
                <path
                  d="M14.1667 16.25C15.0871 16.25 15.8333 15.5038 15.8333 14.5833C15.8333 13.6629 15.0871 12.9167 14.1667 12.9167C13.2462 12.9167 12.5 13.6629 12.5 14.5833C12.5 15.5038 13.2462 16.25 14.1667 16.25Z"
                  fill="currentColor"
                />
              </svg>
              {t('youCanBuyThisCarOnline', { ns: 'other' })}
            </EcomBanner>
          )}
        </Footer>
      )}
    </Wrapper>
  );
};

export default ProductCard;
